import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import type { ContributeReadiness } from '@utils/api/custom-models/use-contribute-segment.tsx';
import {
  contributionProgress,
  contributionResultMessage
} from '@utils/editors/contribution-messages.ts';

type Props = {
  result: ContributeReadiness;
  modelName?: string;
  onClose: () => void;
};

/** What the contribution did, and how far the model is from its next round. */
export const AddToTrainingResult = ({ result, modelName, onClose }: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const accent = getColour('activeText');
  const muted = getColour('inactiveText');
  const message = contributionResultMessage(result, modelName);
  const { pct, hasEnough, current, required } = contributionProgress(result);

  return (
    <div
      className="flex flex-col items-center gap-4 py-4 text-center"
      style={{ color: getColour('normalText') }}
    >
      <span
        className="flex h-11 w-11 items-center justify-center rounded-full"
        style={{ backgroundColor: getColour('successSoft') }}
      >
        <CheckCircle2 size={22} style={{ color: getColour('success') }} />
      </span>
      <p className="text-sm font-medium px-2">
        {t(message.key, message.values)}
      </p>

      <div className="w-full flex flex-col gap-1.5">
        <div
          className="flex justify-between text-[11px]"
          style={{ color: muted }}
        >
          <span>{t('contribution-progress-label')}</span>
          <span className="tabular-nums">
            {hasEnough
              ? t('contribution-progress-ready')
              : t('contribution-progress-value', { current, required })}
          </span>
        </div>
        <div
          className="h-1.5 w-full rounded-full"
          style={{ backgroundColor: getColour('border') }}
        >
          <div
            className="h-1.5 rounded-full transition-all"
            style={{ width: `${pct}%`, backgroundColor: accent }}
          />
        </div>
        <p className="text-[11px] text-left" style={{ color: muted }}>
          {hasEnough
            ? t('contribution-ready-to-train')
            : t('contribution-no-training-started-short')}
        </p>
      </div>

      <Button
        variant="secondary"
        size="small"
        label={t('close')}
        onClick={onClose}
        className="self-end"
      />
    </div>
  );
};
